import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, formatINR, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [addr, setAddr] = useState({ full_name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "" });

  useEffect(() => {
    if (placed) return;
    if (!loading && !user) nav("/login?redirect=/checkout");
    if (items.length === 0 && !busy) nav("/cart");
    if (user) setAddr((a) => ({ ...a, full_name: a.full_name || user.name || "" }));
  }, [user, loading, items.length, placed]);

  const place = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const orderPayload = { items, address: addr, total: subtotal };
      const { data } = await axios.post(`${API}/orders`, orderPayload);
      // mock razorpay verify
      await axios.post(`${API}/orders/${data.order_id}/verify-payment`);
      setPlaced(true);
      toast.success("Order confirmed. Studio will reach out.");
      nav(`/order/${data.order_id}`, { replace: true });
      clear();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Order failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-in max-w-[1200px] mx-auto px-6 md:px-12 py-16" data-testid="checkout-page">
      <div className="font-micro text-[#8A8680] mb-4">Final Step</div>
      <h1 className="font-serif text-[#EDE4D3] text-5xl mb-12">Checkout</h1>

      <form onSubmit={place} className="grid md:grid-cols-[1.4fr_1fr] gap-16">
        <div>
          <div className="font-micro text-[#8A8680] mb-6">Ship To</div>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-5">
              <input className="input-teak" placeholder="Full name" value={addr.full_name} onChange={(e) => setAddr({ ...addr, full_name: e.target.value })} required data-testid="checkout-name" />
              <input className="input-teak" placeholder="Phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} required data-testid="checkout-phone" />
            </div>
            <input className="input-teak" placeholder="Address line 1" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} required data-testid="checkout-line1" />
            <input className="input-teak" placeholder="Address line 2 (optional)" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} data-testid="checkout-line2" />
            <div className="grid grid-cols-3 gap-5">
              <input className="input-teak" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} required data-testid="checkout-city" />
              <input className="input-teak" placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} required data-testid="checkout-state" />
              <input className="input-teak" placeholder="Pincode" value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} required data-testid="checkout-pincode" />
            </div>
          </div>

          <div className="mt-16">
            <div className="font-micro text-[#8A8680] mb-4">Payment</div>
            <div className="p-6 flex items-center justify-between" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
              <div>
                <div className="font-serif text-[#EDE4D3] text-xl">Razorpay</div>
                <div className="font-micro text-[#8A8680] mt-1">UPI · Cards · Netbanking · Wallets (Test Mode)</div>
              </div>
              <div className="ribbon-bronze">Secure</div>
            </div>
          </div>
        </div>

        <div className="md:sticky md:top-32 md:self-start p-8" style={{ background: "#141414", border: "1px solid var(--hairline)" }}>
          <div className="font-micro text-[#8A8680] mb-6">Your Order</div>
          <div className="space-y-4 mb-6">
            {items.map((it, i) => (
              <div key={i} className="flex gap-4">
                {it.image && <img src={it.image} className="w-16 h-20 object-cover" alt="" />}
                <div className="flex-1">
                  <div className="font-serif text-[#EDE4D3]">{it.name}</div>
                  <div className="font-micro text-[#8A8680] mt-1">× {it.quantity}</div>
                </div>
                <div className="font-serif text-[#EDE4D3]">{formatINR(it.unit_price * it.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-between py-4" style={{ borderTop: "1px solid var(--hairline)" }}>
            <span className="font-serif text-[#EDE4D3] text-xl">Total</span>
            <span className="font-serif text-[#EDE4D3] text-xl">{formatINR(subtotal)}</span>
          </div>
          <button type="submit" className="btn-bronze w-full mt-6" disabled={busy} data-testid="checkout-place-order">
            {busy ? "Processing…" : `Place Order · ${formatINR(subtotal)}`}
          </button>
          <div className="font-micro text-[#8A8680] mt-4 text-center">Payment is currently mocked for v1.</div>
        </div>
      </form>
    </div>
  );
}
